<template>
  <a-popover
    placement="bottom"
    v-model:open="visiblePopover"
    overlayClassName="hubspot-events-popover"
  >
    <template #content>
      <div class="popover-container">
        <h4 class="title">
          Eventos de HubSpot en cola ({{ hubspotEvents.length }})
        </h4>

        <div v-for="event in visibleEvents" :key="event.id" class="event-item">
          <div class="event-header">
            <span class="event-name">{{ INTEGRATION_CONFIG[event.functionality]?.eventText }}</span>
            <a-tag
              :color="getStatusColor(event.state)"
              class="status-tag"
            >
              {{ getStatusText(event.state) }}
            </a-tag>
          </div>

          <div class="event-info">
            {{ formatDate(event.scheduled) }} | {{ formatNumber(event.contacts_count) }} contactos
            <a-tooltip>
              <template #title>
                Se seleccionaron {{ formatCount(event.contacts_count) }} contactos creados desde HubSpot que tienen completas las columnas 
                Empresa, Campaña y las marcadas como Dato requerido en las plantillas de contactos de BeexCC.
              </template>
              <InfoCircleOutlined />
            </a-tooltip>
          </div>

          <a-tooltip v-if="event.state != StatusHubpspotEvent.InProgress">
            <template #title>
              Puedes cancelar el evento antes de la fecha y hora programada. Una vez que inicie, no se podrá detener ni deshacer.
            </template>
            <a-button
              type="link"
              class="button-cancel"
              :disabled="dayjs(event.scheduled).isSameOrBefore(dayjs())"
              @click="handleCancel(event.id)"
            >
              <CloseCircleOutlined style="margin-right: 2px" />
              <span style="margin-left: 4px">Cancelar</span>
            </a-button>
          </a-tooltip>

          <span class="event-percent" v-else>
            {{ event.percent }}% | {{ INTEGRATION_CONFIG[event.functionality]?.eventProcess }}
          </span>
        </div>
      </div>

      <div
        v-if="remainingCount > 0"
        class="event-more"
        @click="openModal"
      >
        <EyeOutlined />
        Ver {{ remainingCount }} más
      </div>
    </template>

    <!-- Lo que mostrará para abrir el popover -->
    <slot>
      <a-button type="link" class="button-events" v-if="inProgress && hubspotEvents.length > 0">
        <span v-if="countInProgress > 0">
          {{ countInProgress }} {{ countInProgress == 1 ? 'evento' : 'eventos' }} en curso
          <LoadingOutlined />
        </span>
        <span v-else>
          {{ hubspotEvents.length }} {{ hubspotEvents.length == 1 ? 'evento' : 'eventos' }} en cola
        </span>
      </a-button>
      <a-button type="link" class="button-events" v-else-if="!inProgress">
        <ClockCircleOutlined />
        {{ hubspotEvents.length }} {{ hubspotEvents.length == 1 ? 'evento' : 'eventos' }}
      </a-button>
    </slot>
  </a-popover>

  <a-modal
    v-model:open="visibleModal"
    title="Eventos de HubSpot"
    :footer="null"
    width="420px"
    centered
  >
    <p class="modal-subtitle">
      Existen {{ hubspotEvents.length }} eventos de HubSpot en cola
    </p>

    <CustomScrollbar height="calc(100vh - 300px)" auto>
      <div
        v-for="event in hubspotEvents"
        :key="event.id"
        class="modal-event-item"
      >
        <div class="event-header">
          <span class="event-name">
            {{ INTEGRATION_CONFIG[event.functionality]?.eventText }}
          </span>

          <a-tag :color="getStatusColor(event.state)">
            {{ getStatusText(event.state) }}
          </a-tag>
        </div>

        <div class="event-info">
          {{ formatDate(event.scheduled) }} |
          {{ formatNumber(event.contacts_count) }} contactos
        </div>

        <a-button
          v-if="event.state !== StatusHubpspotEvent.InProgress"
          type="link"
          class="button-cancel"
          @click="handleCancel(event.id)"
        >
          <CloseCircleOutlined />
          Cancelar
        </a-button>

        <div
          v-else
          class="event-percent"
        >
          {{ event.percent }}% | {{ INTEGRATION_CONFIG[event.functionality]?.eventProcess }}
        </div>
      </div>
    </CustomScrollbar>

    <div class="modal-footer">
      <a-button @click="visibleModal = false">
        Cerrar detalles
      </a-button>
    </div>
  </a-modal>

</template>

<script setup>
import { StatusHubpspotEvent, StatusHubpspotEventColor, StatusHubpspotEventLabel } from "@/@types/integrations";
import { ClockCircleOutlined, CloseCircleOutlined, EyeOutlined, InfoCircleOutlined, LoadingOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { defineProps, defineEmits, createVNode, h, ref, onMounted, watch, onUnmounted } from "vue";
import { INTEGRATION_CONFIG } from "@/app/integrations/utils/enums";
import { formatCount, formatNumber } from "@/app/integrations/utils/formats";
import { computed } from "vue";
import { message, Modal } from "ant-design-vue";
import dayjs from "dayjs";
import { hubspotManager } from "@/app/integrations/services/HubspotManager";
import httpManager from "@/app/shared/utils/httpManager";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

const MAX_VISIBLE = 3;

const POLL_INTERVAL = 5000;
let pollInterval = null;

const props = defineProps({
  inProgress: { type: Boolean, default: false },
});

const startPolling = async () => {
  if (pollInterval) return;

  // fetch inmediato
  await getHubspotEvents();

  pollInterval = setInterval(() => {
    getHubspotEvents();
  }, POLL_INTERVAL);
};

const stopPolling = () => {
  if (!pollInterval) return;

  clearInterval(pollInterval);
  pollInterval = null;
};

const visiblePopover = ref(false);
const visibleModal = ref(false);
const hubspotEvents = ref([]);
const countInProgress = ref([]);

watch(
  [visiblePopover, visibleModal],
  async ([isPopoverOpen, isModalOpen]) => {
    if (isPopoverOpen || isModalOpen || props.inProgress) {
      // FETCH INMEDIATO
      await getHubspotEvents();

      // luego polling
      startPolling();
    } else {
      stopPolling();
    }
  }
);


const visibleEvents = computed(() => {
  return hubspotEvents.value?.slice(0, MAX_VISIBLE);
});

const remainingCount = computed(() => {
  return Math.max(hubspotEvents.value?.length - MAX_VISIBLE, 0);
});

// const props = defineProps({
//   events: {
//     type: Array,
//     required: true,
//     default: () => [],
//   },
// });

const emit = defineEmits(["onCancel"]);

const emitCancel = (event) => {
  emit("onCancel", event);
};

const getStatusColor = (status) => {
  return StatusHubpspotEventColor[status] || "default";
};

const getStatusText = (status) => {
  return StatusHubpspotEventLabel[status] || "Desconocido";
};

const formatDate = (isoString) => {
  if (!isoString) return "";

  // 2026-01-17T03:00:00Z
  const [datePart, timePart] = isoString.replace("Z", "").split("T");
  const [year, month, day] = datePart.split("-");
  const [hour, minute] = timePart.split(":");

  return `${day}/${month}/${year} ${hour}:${minute}`;
};

const handleCancel = (id) => {
  visiblePopover.value = false;
  Modal.confirm({
    title: "Estás a punto de cancelar este evento programado, ¿deseas continuar?",
    content: h("span", [
      "Al cancelar, el evento se eliminará y los contactos seleccionados serán liberados para poder ser incluidos en nuevos eventos. Recuerda que esta acción ",
      h(
        "span",
        { style: { fontWeight: 600 } },
        "no se podrá deshacer."
      ),
    ]),
    icon: createVNode(QuestionCircleOutlined),
    onOk: () => {
      handleCancelEvent(id);
    },
    centered: true,
    okText: "Si, cancelar",
    cancelText: "No, conservar evento",
  });
};

const handleCancelEvent = async (id) => {
  try {
    const response = await hubspotManager.cancelHubspotEvent(id);
    message.success("Se ha cancelado un evento programado con éxito.")
  } catch (error) {
    const errorMessage = error?.getErrorMessage?.() || "Ha courrido un error";
    message.error(errorMessage);
  }
}

const getHubspotEvents = async () => {
  const response = await hubspotManager.getHubspotConfig();
  if (!response?.functionalities) return [];

  if (props.inProgress) {
    hubspotEvents.value = response.functionalities.flatMap((f) => 
      f.events.filter((el) => [StatusHubpspotEvent.InProgress, StatusHubpspotEvent.Programmed].includes(el.state))
        .map((el) => ({...el, functionality: f.code })) || []
    );
    let inProgressCount = hubspotEvents.value.filter(e => e.state === StatusHubpspotEvent.InProgress).length;
    countInProgress.value = inProgressCount;
    return;
  }

  hubspotEvents.value = response.functionalities.flatMap((f) => 
    f.events.map((el) => ({...el, functionality: f.code })) || []
  );
};

const openModal = () => {
  visiblePopover.value = false
  visibleModal.value = true
}

onMounted(async () => {
  await getHubspotEvents()
})

onUnmounted(() => {
  stopPolling();
});
</script>

<style scoped lang="sass">
.popover-container
  width: 280px
  // padding: 8px
  color: #262626E0

.title
  font-size: 14px
  font-weight: 600
  margin-bottom: 4px

.event-item
  // border-bottom: 1px solid #f0f0f0
  padding: 10px 0
  &:not(:last-child)
    border-bottom: 1px solid #f0f0f0
  &:last-child
    padding: 10px 0 0

.event-header
  display: flex
  justify-content: space-between
  align-items: center

.event-name
  font-weight: 400

.status-tag
  font-size: 11px

.event-info
  color: #8c8c8c
  font-size: 12px
  margin: 2px 0 0

.event-percent
  font-size: 12px
  color: #1677ff

.button
  &-cancel
    padding: 0
    font-size: 12px
    height: auto
  &-events
    padding: 0

.event-more
  width: calc(100% + 24px)
  margin-left: -12px
  margin-right: -12px

  display: flex
  align-items: center
  justify-content: center
  gap: 6px

  padding: 8px 12px
  border-top: 1px solid #f0f0f0

  font-size: 13px
  color: #1677ff
  cursor: pointer
  margin-top: 15px

  &:hover
    text-decoration: underline

.modal-subtitle
  font-size: 14px
  color: #262626E0
  font-weight: 500
  margin-top: 20px
  margin-bottom: 5px

.modal-event-item
  padding: 12px 0
  &:not(:last-child)
    border-bottom: 1px solid #f0f0f0

  // &:last-child
  //   border-bottom: none

.modal-footer
  margin-top: 16px
  display: flex
  justify-content: center

strong
  font-weight: 600
</style>
